import React from "react";
import { CurrencySymbol } from "../../../utils/enums";

const toCount = (value) => {
  const num = Number(value);
  return Number.isFinite(num) ? num : 0;
};

const formatSoldPrice = (value) => {
  const amount = toCount(value);
  if (amount === 0) return `${CurrencySymbol.NGN} 0`;
  if (amount >= 1_000_000) {
    return `${CurrencySymbol.NGN} ${(amount / 1_000_000).toFixed(1).replace(/\.0$/, "")}m`;
  }
  if (amount >= 10_000) {
    return `${CurrencySymbol.NGN} ${Math.round(amount / 1_000)}k`;
  }
  return `${CurrencySymbol.NGN} ${Math.round(amount / 1_000)}`;
};

const formatRentPrice = (value) => {
  const amount = toCount(value);
  if (amount === 0) return `${CurrencySymbol.NGN} 0pw`;
  return `${CurrencySymbol.NGN} ${amount.toLocaleString("en-US")}pw`;
};

const OverviewCard = ({ AgencyData, MarketPerformanceData }) => {
  const agencyName =
    AgencyData?.[0]?.principal_name ||
    AgencyData?.[0]?.agency_name ||
    "this agency";

  const market = MarketPerformanceData ?? {};
  const sales = market?.sales ?? {};
  const rent = market?.rent ?? {};
  const summary = market?.summary ?? {};

  const periodMonths = toCount(market?.periodMonths) || 12;
  const propertiesSold = toCount(
    summary?.propertiesSold ?? sales?.propertiesSold,
  );
  const propertiesLeased = toCount(
    summary?.propertiesLeased ?? rent?.propertiesLeased,
  );

  const salesPerformance = [
    {
      value: formatSoldPrice(sales?.medianSoldPrice),
      label: "Median sold price",
    },
    {
      value: String(toCount(sales?.medianDaysAdvertised)),
      label: "Median days advertised",
    },
    {
      value: String(toCount(sales?.propertiesSold)),
      label: "Properties sold",
    },
    {
      value: String(toCount(sales?.propertiesForSale)),
      label: "Properties for sale",
    },
  ];

  const rentPerformance = [
    {
      value: formatRentPrice(rent?.medianRentPrice),
      label: "Median rent price",
    },
    {
      value: String(toCount(rent?.medianDaysAdvertised)),
      label: "Median days advertised",
    },
    {
      value: String(toCount(rent?.propertiesLeased)),
      label: "Properties leased",
    },
    {
      value: String(toCount(rent?.propertiesForLease)),
      label: "Properties for lease",
    },
  ];

  return (
    <div className="flex flex-col gap-3 bg-white relative mx-auto p-6 rounded-2xl border border-[#E5E5E5]">
      <h1 className="text-2xl font-semibold text-[#000929] flex items-center relative">
        Market performance snapshot
      </h1>
      <p className="text-[#000929] text-sm font-normal">
        In the last {periodMonths} months, {agencyName} has sold{" "}
        {propertiesSold} {propertiesSold === 1 ? "property" : "properties"} and
        leased {propertiesLeased}{" "}
        {propertiesLeased === 1 ? "property" : "properties"} on
        myrealestate-ng.com.
      </p>

      <div>
        <h3 className="text-[#000929] uppercase text-sm tracking-wider my-1">
          Sales performance
        </h3>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          {salesPerformance.map((item, index) => (
            <div
              key={index}
              className="text-[#000929] gap-2 px-4 py-5 rounded-xl flex flex-col items-start bg-[#F3F6F9]"
            >
              <div className="text-[26px] font-semibold">{item.value}</div>
              <div className="text-base opacity-75">{item.label}</div>
            </div>
          ))}
        </div>
      </div>

      <div>
        <h3 className="text-[#000929] uppercase text-sm tracking-wider my-1">
          Rent performance
        </h3>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          {rentPerformance.map((item, index) => (
            <div
              key={index}
              className="text-[#000929] gap-2 px-4 py-5 rounded-xl flex flex-col items-start bg-[#F3F6F9]"
            >
              <div className="text-[26px] font-semibold">{item.value}</div>
              <div className="text-base opacity-75">{item.label}</div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default OverviewCard;
